import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-brand-bg flex flex-col overflow-x-hidden">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute top-[-10%] left-[-5%] w-[55vw] h-[55vw] bg-orange-500/[0.07] rounded-full blur-3xl" />
        <div className="absolute top-[20%] right-[-10%] w-[60vw] h-[60vw] bg-red-500/[0.06] rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[20%] w-[50vw] h-[50vw] bg-orange-500/[0.05] rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />

        <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 text-sm hover:text-orange-400 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-slate-400 text-sm mb-10">Last updated: January 2025</p>

          <div className="space-y-8 text-slate-300 text-sm sm:text-base leading-relaxed [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-semibold [&_h2]:text-white [&_h2]:mb-4 [&_h2]:mt-8 [&_h3]:text-base [&_h3]:sm:text-lg [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-3 [&_h3]:mt-6 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-2 [&_a]:text-orange-400 [&_a]:hover:underline [&_strong]:text-white">
            <section>
              <h2>1. Introduction</h2>
              <p>
                Valuebell ("we", "our", or "us") operates VALUEBOT, a podcast transcription and show notes service.
                This Privacy Policy explains how we collect, use, and protect your information when you use our service.
              </p>
            </section>

            <section>
              <h2>2. Information We Collect</h2>

              <h3>Account Information</h3>
              <p>When you sign in with Google, we receive:</p>
              <ul>
                <li>Your name and email address</li>
                <li>Profile picture (for display purposes only)</li>
              </ul>

              <h3>Content You Submit</h3>
              <p>When you use VALUEBOT, we process:</p>
              <ul>
                <li>Google Drive file URLs you provide for transcription</li>
                <li>The audio/video content of those files (temporarily, for processing)</li>
              </ul>

              <h3>Preferences</h3>
              <ul>
                <li>Newsletter opt-in preference</li>
              </ul>

              <h3>Usage Data</h3>
              <p>We automatically collect basic usage information such as job submission times and processing status.</p>
            </section>

            <section>
              <h2>3. How We Use Your Information</h2>
              <ul>
                <li><strong>Processing your requests:</strong> To transcribe your audio/video files and generate show notes</li>
                <li><strong>Sending results:</strong> To email you links to your completed transcripts and show notes</li>
                <li><strong>Newsletter:</strong> If you opt in, to send you podcast tips and Valuebell news</li>
                <li><strong>Service improvement:</strong> To understand usage patterns and improve our service</li>
              </ul>
            </section>

            <section>
              <h2>4. Third-Party Services</h2>
              <p>To provide our service, we use the following third-party providers who may process your data:</p>

              <h3>ElevenLabs</h3>
              <p>
                We use ElevenLabs for speech-to-text transcription. Your audio content is sent to their servers for processing.
                See their <a href="https://elevenlabs.io/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>

              <h3>Google Gemini AI</h3>
              <p>
                We use Google's Gemini AI to generate show notes (episode titles, descriptions, chapter markers, etc.) from your transcripts.
                See Google's <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>

              <h3>Firebase (Google)</h3>
              <p>
                We use Firebase for user authentication. Your Google account information is processed through Firebase Authentication.
              </p>

              <h3>Google Cloud Platform</h3>
              <p>
                Our processing infrastructure runs on Google Cloud Platform. Your files are temporarily stored and processed on Google Cloud servers located in the Middle East (Tel Aviv) region.
              </p>

              <h3>Google Drive</h3>
              <p>
                Output files (transcripts and show notes) are stored in Google Drive folders that we share with you.
              </p>
            </section>

            <section>
              <h2>5. Data Retention</h2>

              <h3>Temporary Processing Files</h3>
              <p>
                Converted audio files and temporary copies used during processing are deleted immediately after your job completes.
              </p>

              <h3>Job Records</h3>
              <p>
                We retain job records (submission time, status, file URLs) in our database for 90 days to provide you with job history and support.
              </p>

              <h3>Output Files</h3>
              <p>
                The episode folder containing your source file, transcripts, and show notes remains in our Google Drive
                until you request deletion. These folders are shared with you via restricted access (only you and our service account can access them).
              </p>
            </section>

            <section>
              <h2>6. Data Security</h2>
              <ul>
                <li>All data is transmitted over HTTPS (encrypted in transit)</li>
                <li>We use Firebase Authentication for secure sign-in</li>
                <li>We do not store your Google password or OAuth credentials</li>
                <li>Output folders are set to restricted access, shared only with your email address</li>
              </ul>
            </section>

            <section>
              <h2>7. Your Rights</h2>
              <p>You have the right to:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of the data we hold about you</li>
                <li><strong>Deletion:</strong> Request deletion of your data by emailing us</li>
                <li><strong>Newsletter opt-out:</strong> Unsubscribe from marketing emails at any time via the link in the email or by contacting us</li>
              </ul>
              <p>
                To exercise these rights, please contact us at <a href="mailto:support@valuebell.studio">support@valuebell.studio</a>.
              </p>
            </section>

            <section>
              <h2>8. Children's Privacy</h2>
              <p>
                VALUEBOT is not intended for use by individuals under 18 years of age. We do not knowingly collect information from children.
              </p>
            </section>

            <section>
              <h2>9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice on our website
                or sending you an email.
              </p>
            </section>

            <section>
              <h2>10. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p>
                <strong>Email:</strong> <a href="mailto:support@valuebell.studio">support@valuebell.studio</a><br />
                <strong>Company:</strong> Valuebell Studio<br />
                <strong>Location:</strong> Israel
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};
